import { inject, Injectable } from '@angular/core';
import { assertGraphqlDataNode, type OfType } from '@reconcept-platform/shared/data-access';
import {
  type CreateWorkspaceInput,
  type CreateWorkspaceMutation,
  type CreateWorkspaceMutationVariables,
  type DestroyWorkspaceMutation,
  type DestroyWorkspaceMutationVariables,
  type UpdateWorkspaceInput,
  type UpdateWorkspaceMutation,
  type UpdateWorkspaceMutationVariables,
  type WorkspaceDetailQueryQuery,
  type WorkspaceDetailQueryQueryVariables,
  type WorkspaceDomainAndSubDomainsQueryQuery,
  type WorkspaceDomainAndSubDomainsQueryQueryVariables,
  type WorkspaceDomainsMenuItemsQueryQuery,
  type WorkspaceDomainsMenuItemsQueryQueryVariables,
  type WorkspaceDomainsQueryQuery,
  type WorkspaceDomainsQueryQueryVariables,
  type WorkspaceFilter,
  type WorkspaceScopeForRoleModalQueryQuery,
  type WorkspaceScopeForRoleModalQueryQueryVariables,
  type WorkspacesQueryQuery,
  type WorkspacesQueryQueryVariables,
  type WorkspaceWithActiveCardsQuery,
  type WorkspaceWithActiveCardsQueryVariables,
  type WorkspaceWithQualityCalendarQuery,
  type WorkspaceWithQualityCalendarQueryVariables,
  type WorkspaceWithRolesQueryQuery,
  type WorkspaceWithRolesQueryQueryVariables,
  type WorkspaceWithSecondaryDashboardsQueryQuery,
  type WorkspaceWithSecondaryDashboardsQueryQueryVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { WorkspaceGraphQLService } from '../../workspace-graphql.service';
import { DashboardWithWorkspacesQuery } from '../dashboard/dashboard.queries';
import { eventCardItemFragment } from '../event-card/event-card.fragments';
import { qualityInstrumentItemFragment } from '../quality-instrument/quality-instrument.fragments';
import { workspaceDetailFragment, workspaceItemFragment, workspaceViewerCanFragment } from './workspace.fragments';
import {
  workspaceDetailQuery,
  workspaceDomainAndSubDomainsQuery,
  workspaceDomainsMenuItemsQuery,
  workspaceDomainsQuery,
  workspaceScopeForRoleModalQuery,
  workspacesQuery,
  workspaceWithActiveCardsQuery,
  workspaceWithRolesQuery,
  workspaceWithSecondaryDashboardsQuery,
} from './workspace.queries';

export type WorkspaceDetailQuery_Workspace = OfType<NonNullable<WorkspaceDetailQueryQuery['node']>, 'Workspace'>;

export type WorkspaceDomainsMenuItemsQueryQuery_Workspace = OfType<
  // @ts-expect-error TS2344 type constraint not satisfied
  WorkspaceDomainsMenuItemsQueryQuery['node'],
  'Workspace'
>;

export type WorkspaceWithQualityCalendarQuery_Workspace = OfType<
  // @ts-expect-error TS2344 type constraint not satisfied
  WorkspaceWithQualityCalendarQuery['node'],
  'Workspace'
>;

export type WorkspaceWithQualityCalendarQuery_QualityInstrument =
  // @ts-expect-error TS2339 property does not exist
  WorkspaceWithQualityCalendarQuery_Workspace['qualityInstruments']['edges'][number]['node'];

export type WorkspaceDomainAndSubDomainsQueryQuery_Subdomain = OfType<
  // @ts-expect-error TS2344 type constraint not satisfied
  WorkspaceDomainAndSubDomainsQueryQuery['node'],
  'Workspace'
  // @ts-expect-error TS2339 property does not exist
>['dashboard']['dashboardFormat']['parentDomains']['nodes'][number]['subDomains']['nodes'][number];

// @ts-expect-error TS2344 type constraint not satisfied
export type WorkspaceWithRolesQuery_Workspace = OfType<WorkspaceWithRolesQueryQuery['node'], 'Workspace'>;

// @ts-expect-error TS2344 type constraint not satisfied
export type WorkspaceScopeForRoleModal_Workspace = OfType<WorkspaceScopeForRoleModalQueryQuery['node'], 'Workspace'>;

export type WorkspaceWithSecondaryDashboardsQueryQuery_Workspace = OfType<
  // @ts-expect-error TS2344 type constraint not satisfied
  WorkspaceWithSecondaryDashboardsQueryQuery['node'],
  'Workspace'
>;

export type WorkspaceDomainsQuery_ParentDomain = OfType<
  // @ts-expect-error TS2344 type constraint not satisfied
  WorkspaceDomainsQueryQuery['node'],
  'Workspace'
  // @ts-expect-error TS2339 property does not exist
>['dashboard']['dashboardFormat']['parentDomains']['nodes'][number];

export type WorkspaceWithActiveCardsQuery_GoalCard = OfType<
  // @ts-expect-error TS2344 type constraint not satisfied
  WorkspaceWithActiveCardsQuery['node'],
  'Workspace'
>['goalCards']['nodes'][number];
export type WorkspaceWithActiveCardsQuery_EventCard = OfType<
  // @ts-expect-error TS2344 type constraint not satisfied
  WorkspaceWithActiveCardsQuery['node'],
  'Workspace'
>['eventCards']['nodes'][number];

// @ts-expect-error TS2339 property does not exist
export type WorkspacesQuery_Workspace = WorkspacesQueryQuery['workspaces']['edges'][number]['node'];
@Injectable({
  providedIn: 'root',
})
export class WorkspaceApi {
  private workspaceGraphQLService = inject(WorkspaceGraphQLService);

  watchAllWorkspaces() {
    return (
      this.workspaceGraphQLService
        .watchQuery<WorkspacesQueryQuery, WorkspacesQueryQueryVariables>({
          query: workspacesQuery,
        })
        // @ts-expect-error TS18047 possibly null | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.workspaces.edges.map((workspace) => workspace.node)))
    );
  }

  watchWorkspace(workspaceId: string): Observable<WorkspaceDetailQuery_Workspace> {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceDetailQueryQuery, WorkspaceDetailQueryQueryVariables>({
        query: workspaceDetailQuery,
        variables: {
          id: workspaceId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Workspace')));
  }

  watchWorkspaceWithSecondaryDashboards(workspaceId: string) {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceWithSecondaryDashboardsQueryQuery, WorkspaceWithSecondaryDashboardsQueryQueryVariables>({
        query: workspaceWithSecondaryDashboardsQuery,
        variables: {
          id: workspaceId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Workspace')));
  }

  watchWorkspaceWithQualityCalendar(workspaceId: string) {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceWithQualityCalendarQuery, WorkspaceWithQualityCalendarQueryVariables>({
        query: gql`
          query WorkspaceWithQualityCalendar($id: ID!) {
            node(id: $id) {
              ... on Workspace {
                ...WorkspaceItemFragment
                ...WorkspaceViewerCanFragment

                accreditationEndDate

                qualityInstruments {
                  edges {
                    firstUpcomingEventCard {
                      ...EventCardItemFragment
                    }

                    mostRecentEventCard {
                      ...EventCardItemFragment
                    }

                    node {
                      ...QualityInstrumentItem
                    }
                  }
                }
              }
            }
          }
          ${workspaceItemFragment}
          ${workspaceViewerCanFragment}
          ${eventCardItemFragment}
          ${qualityInstrumentItemFragment}
        `,
        variables: {
          id: workspaceId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Workspace')));
  }

  watchWorkspaceWithRoles(workspaceId: string) {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceWithRolesQueryQuery, WorkspaceWithRolesQueryQueryVariables>({
        query: workspaceWithRolesQuery,
        variables: {
          id: workspaceId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Workspace')));
  }

  watchWorkspaceScopeForRoleModal(workspaceId: string) {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceScopeForRoleModalQueryQuery, WorkspaceScopeForRoleModalQueryQueryVariables>({
        query: workspaceScopeForRoleModalQuery,
        variables: {
          id: workspaceId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Workspace')));
  }

  watchWorkspaceDomainAndSubDomains(workspaceId: string) {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceDomainAndSubDomainsQueryQuery, WorkspaceDomainAndSubDomainsQueryQueryVariables>({
        query: workspaceDomainAndSubDomainsQuery,
        variables: {
          id: workspaceId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Workspace')));
  }

  watchWorkspaceDetailMenuItems(workspaceId: string, workspaceFilter: WorkspaceFilter) {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceDomainsMenuItemsQueryQuery, WorkspaceDomainsMenuItemsQueryQueryVariables>({
        query: workspaceDomainsMenuItemsQuery,
        variables: {
          id: workspaceId,
          filter: workspaceFilter,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Workspace')));
  }

  watchWorkspaceDomains(workspaceId: string) {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceDomainsQueryQuery, WorkspaceDomainsQueryQueryVariables>({
        query: workspaceDomainsQuery,
        variables: {
          id: workspaceId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Workspace')));
  }

  updateWorkspace(updateWorkspaceInput: UpdateWorkspaceInput) {
    return (
      this.workspaceGraphQLService
        .mutateV2<UpdateWorkspaceMutation, UpdateWorkspaceMutationVariables>({
          mutation: gql`
            mutation UpdateWorkspace($input: UpdateWorkspaceInput!) {
              updateWorkspace(input: $input) {
                workspace {
                  ...WorkspaceDetailFragment
                }
                errors {
                  path
                  message
                }
              }
            }
            ${workspaceDetailFragment}
          `,
          variables: {
            input: updateWorkspaceInput,
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateWorkspace.workspace))
    );
  }

  createWorkspace(createWorkspace: CreateWorkspaceInput) {
    return (
      this.workspaceGraphQLService
        .mutateV2<CreateWorkspaceMutation, CreateWorkspaceMutationVariables>({
          mutation: gql`
            mutation CreateWorkspace($input: CreateWorkspaceInput!) {
              createWorkspace(input: $input) {
                workspace {
                  ...WorkspaceItemFragment
                }
                errors {
                  path
                  message
                }
              }
            }
            ${workspaceItemFragment}
          `,
          variables: {
            input: createWorkspace,
          },
          refetchQueries: [
            {
              query: DashboardWithWorkspacesQuery,
              variables: {
                id: createWorkspace.dashboardId,
              },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createWorkspace.workspace))
    );
  }

  destroyWorkspace(id: string, dashboardId: string) {
    return this.workspaceGraphQLService.mutateV2<DestroyWorkspaceMutation, DestroyWorkspaceMutationVariables>({
      mutation: gql`
        mutation DestroyWorkspace($input: DestroyWorkspaceInput!) {
          destroyWorkspace(input: $input) {
            errors {
              path
              message
            }
          }
        }
      `,
      variables: {
        input: {
          workspaceId: id,
        },
      },
      refetchQueries: [
        {
          query: DashboardWithWorkspacesQuery,
          variables: {
            id: dashboardId,
          },
        },
      ],
    });
  }

  watchWorkspaceWithCards(workspaceId: string) {
    return this.workspaceGraphQLService
      .watchQuery<WorkspaceWithActiveCardsQuery, WorkspaceWithActiveCardsQueryVariables>({
        query: workspaceWithActiveCardsQuery,
        variables: {
          workspaceId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'Workspace')));
  }
}
